import * as z from "zod";
import { CardInfo, CardSummary, ScpData, SearchLinks } from "./card";
import { isoDate } from "./common";

export const SCAN_REVIEWS = ["PENDING", "CONFIRMED", "REJECTED"] as const;
export type ScanReview = (typeof SCAN_REVIEWS)[number];

export const PRICING_STATUSES = ["priced", "no_match", "degraded"] as const;
export const MATCH_KINDS = ["PRODUCT", "IDENTITY", "CREATED", "NONE"] as const;

/** One product the pricing lookup weighed for a scan, as the pipeline streams it. */
export const ProductCandidate = z.object({
  /** The product as the pricing source returned it; its `id` is what a pick names. */
  product: ScpData,
  /** 0 is the pick. */
  rank: z.number(),
  /** 0–100. */
  confidence: z.number().nullable(),
  /** Listings eBay's visual search tagged with this product. */
  listingVotes: z.number(),
  /** Photos of listings of this product. */
  images: z.array(z.string()),
});
export type ProductCandidate = z.output<typeof ProductCandidate>;

/** A stored candidate: the streamed one plus whether the shop stocks it. */
export const ScanCandidate = ProductCandidate.extend({
  matchedCardId: z.string().nullable(),
  matchKind: z.enum(["PRODUCT", "NONE"]),
});
export type ScanCandidate = z.output<typeof ScanCandidate>;

export const Scan = z.object({
  recognitionOutcome: z.enum(["recognized", "no_match"]).nullable().optional(),
  id: z.string(),
  storeId: z.string(),
  locationId: z.string().nullable(),
  kind: z.enum(["OWNER", "CUSTOMER"]),
  customerId: z.string().nullable(),
  imageUrl: z.string(),
  backImageUrl: z.string().nullable(),
  /** The reading in force: the reviewer's edits over the model's. */
  cardInfo: CardInfo.nullable(),
  /** The model's own output, never rewritten. */
  modelCardInfo: CardInfo.nullable(),
  scpData: ScpData.nullable(),
  /** Every product the lookup weighed, the pick first. Empty when it weighed none or predates the list. */
  candidates: z.array(ScanCandidate).default([]),
  searchLinks: SearchLinks.nullable(),
  pricingStatus: z.enum(PRICING_STATUSES).nullable(),
  scanProgress: z.string().nullable(),
  scanProgressAt: isoDate.nullable(),
  review: z.enum(SCAN_REVIEWS),
  matchedCardId: z.string().nullable(),
  matchKind: z.enum(MATCH_KINDS),
  /** Null until confirmed. */
  cardId: z.string().nullable(),
  card: CardSummary.nullable(),
  estimatedPriceCents: z.number().nullable(),
  estimatedPriceFrom: z.string().nullable(),
  estimatedPriceGrade: z.string().nullable(),
  reviewedAt: isoDate.nullable(),
  heldCopy: z.boolean().nullable(),
  /** The customer quote's standing; expired once past its deadline. */
  quoteState: z.enum(["active", "expired", "sold", "withdrawn"]).nullable(),
  quotePriceCents: z.number().nullable(),
  quoteExpiresAt: isoDate.nullable(),
  timestamp: isoDate,
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type Scan = z.output<typeof Scan>;

/** The images to scan: hosted URLs or `data:` URLs, as the SPA sends them. */
export interface ScanStart {
  enableNoMatch?: boolean;
  attemptId?: string;
  imageUrl: string;
  backImageUrl?: string;
  /** Staff only: scan for a counter (POS) customer the shop created. */
  customerId?: string;
}

export interface ScanListQuery {
  enableNoMatch?: boolean;
  review?: ScanReview;
  locationId?: string;
  customerId?: string;
  updatedAfter?: Date;
}

export const ScanEdit = z.object({
  scan: Scan,
  /** Whether the edit forced a fresh pricing lookup. */
  repriced: z.boolean(),
  repricedBecause: z.array(z.string()),
});
export type ScanEdit = z.output<typeof ScanEdit>;

/** What to set on the card a confirmation creates or matches. */
export interface ScanConfirmOptions {
  corrections?: Partial<CardInfo>;
  /** Below this the shop does not sell. */
  floorPriceCents?: number;
  /** The asking price. */
  priceCents?: number;
  stock?: { quantity?: number; locationId?: string | null; costCents?: number | null; certNumber?: string | null };
}

export const ScanConfirmation = z.object({
  cardId: z.string(),
  stockId: z.string().nullable(),
  offerId: z.string().nullable(),
});
export type ScanConfirmation = z.output<typeof ScanConfirmation>;

export const REVIEW_BUCKETS = ["new", "exists", "unvetted"] as const;

export const CardFieldDifference = z.object({
  field: z.string(),
  scan: z.string(),
  card: z.string(),
});
export type CardFieldDifference = z.output<typeof CardFieldDifference>;

/**
 * Pending scans that resolve to one identity. `new`: confirming creates a card.
 * `exists`: a card a person approved. `unvetted`: a card a customer's scan
 * created that nobody has looked at yet.
 */
export const ScanReviewGroup = z.object({
  key: z.string(),
  bucket: z.enum(REVIEW_BUCKETS),
  scanIds: z.array(z.string()),
  count: z.number(),
  sample: Scan,
  card: CardSummary.nullable(),
  /** Where the reading and the proposed card disagree. Empty means they agree. */
  differences: z.array(CardFieldDifference),
});
export type ScanReviewGroup = z.output<typeof ScanReviewGroup>;

export const SCAN_STATES_IN_SHOP = ["pending", "approved", "in-cart", "paid", "rejected", "expired"] as const;

/** A customer's own scan in a shop, with the state the buyer sees. */
export const MyScan = Scan.extend({
  stateInShop: z.enum(SCAN_STATES_IN_SHOP),
  priceCents: z.number().nullable(),
  expiresAt: isoDate.nullable(),
  offerId: z.string().nullable(),
});
export type MyScan = z.output<typeof MyScan>;

// ─── The scan pipeline's live stream ─────────────────────────────────────────

export const SCAN_STAGES = ["preparation", "identification", "pricing", "persistence"] as const;
export type ScanStage = (typeof SCAN_STAGES)[number];

/**
 * One frame of `startStream`.
 *
 * A discriminated union on `type`, reassembled from the SSE frame: the backend
 * writes the name on an `event:` line and the payload on a `data:` line, and the
 * JSON carries no `type` of its own.
 *
 * `scan.error` is a **frame, not a rejection**. Once the stream's headers are
 * out there is no status left to fail with, so a pipeline failure arrives here
 * like any other event.
 */
export const ScanStreamEvent = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("scan.started"),
    data: z.object({ stage: z.enum(SCAN_STAGES), scanId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("scan.prepared"),
    data: z.object({ imageUrl: z.string(), backImageUrl: z.string().nullable() }),
  }),
  z.object({
    type: z.literal("scan.identified"),
    data: z.object({ cardInfo: z.record(z.string(), z.unknown()) }),
  }),
  z.object({
    type: z.literal("scan.priced"),
    data: z.object({
      scpData: ScpData.nullable(),
      candidates: z.array(ProductCandidate).default([]),
      searchLinks: SearchLinks,
      searchOnEbay: z.string(),
      /** The pricing stage runs on every scan, so the event alone says nothing about whether a price was found. */
      pricingDegraded: z.boolean(),
      pricingStatus: z.enum(["priced", "no_match", "degraded"]),
    }),
  }),
  z.object({ type: z.literal("scan.completed"), data: Scan }),
  z.object({
    type: z.literal("scan.error"),
    data: z.object({ error: z.string(), stage: z.enum(SCAN_STAGES), terminal: z.boolean().optional() }),
  }),
]);
export type ScanStreamEvent = z.output<typeof ScanStreamEvent>;

export function newScanAttemptId(): string {
  if (typeof crypto?.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
